import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTheme } from "@emotion/react";
import { createTheme } from "@mui/material";
import styled from "@emotion/styled";
import { dataGridClasses } from "./DataGrid.constants.ts";

import {
  isGroupNode,
  isLeafNode,
  isLastRowInGroup,
  computePageSize,
} from "./DataGrid.utils.ts";
import { deDE, enUS } from "@mui/x-data-grid/locales";
import { DataGridStoreProvider } from "./DataGridStoreProvider.tsx";
import { DataGridStore } from "./DataGridStore.ts";

import { DataGridErrorOverlay } from "./DataGridErrorOverlay.tsx";

import {
  StyledEngineProvider,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";

import {
  DataGridPremium as MuiDataGridPremium,
  GRID_TREE_DATA_GROUPING_FIELD,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  gridClasses,
  useGridApiRef,
  GridColumnMenu as MuiGridColumnMenu,
  type GridColType,
  type DataGridPremiumProps,
  type GridFilterModel,
  type GridColumnMenuProps,
} from "@mui/x-data-grid-premium";

import {
  dividerClasses,
  listClasses,
  menuItemClasses,
  listItemIconClasses,
  listItemTextClasses,
  typographyClasses,
} from "@mui/material";

import type {
  GridSlotProps,
  DataGridPremiumProps as MuiDataGridPremiumProps,
  GridColDef as MuiGridColDef,
  GridRowClassNameParams,
  GridCellParams,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";

import type { JSXElementConstructor, ReactNode } from "react";
import type { GridPremiumSlotsComponent } from "@mui/x-data-grid-premium/models/gridPremiumSlotsComponent";

import { DataGridNoRowsOverlay } from "src/components/DataGrid/DataGridNoRowsOverlay.tsx";
import type { SelectProps } from "src/components/SelectV2/SelectV2.types.ts";

import { DataGridNoResultsOverlay } from "src/components/DataGrid/DataGridNoResultsOverlay.tsx";
import { i18n as i18nInstance } from "src/lib/i18next/i18next.ts";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import * as styles from "./DataGrid.style.ts";
import { useTranslation } from "react-i18next";

export type HeaderFilterSelectOptions =
  | SelectProps<string>["options"]
  | {
      resource: string;
      field: string;
    };

export type GridColDef<R extends GridValidRowModel = GridValidRowModel> = Omit<
  MuiGridColDef<R>,
  "type"
> & {
  backendFilterName?: string;
  backendSortName?: string;
  type?: GridColType | "dateRange" | "multiSelect";
  defaultFilterOperator?: TextFilterModeEnum;
  headerFilterSelectProps?: Pick<
    SelectProps<string>,
    "placeholder" | "defaultValue" | "optionsMinWidth"
  > & {
    options?: HeaderFilterSelectOptions;
    serverOptions?: HeaderFilterSelectServerOptions;
  };
};

export type HeaderFilterSelectServerOptions = {
  resource: string;
  filter?: GridFilterModel["items"];
  sort?: GridSortModel;
  processOptions?: (
    options: Required<SelectV2Props<any>>["options"],
  ) => SelectV2Props<any>["options"];
  include?: string[];
  labelProp?: SelectLabelProp<any>;
  valueProp?: SelectValueProp<any>;
};

declare module "@mui/x-data-grid-premium" {
  interface NoRowsOverlayPropsOverrides {
    title?: string;
    Image?: JSXElementConstructor<any>;
  }
}

import { DataGridToolbar } from "src/components/DataGrid/DataGridToolbar.tsx";

import {
  type DataGridContextMenuProps,
  useDataGridRowContextMenu,
} from "src/components/DataGrid/useDataGridRowContextMenu.tsx";

import type { i18n as I18n } from "i18next";
import { DataGridCheckbox } from "src/components/DataGrid/DataGridCheckbox.tsx";
import { DataGridPopper } from "src/components/DataGrid/DataGridPopper.tsx";
import { DataGridEnableDataLoadingOverlay } from "src/components/DataGrid/DataGridEnableDataLoadingOverlay.tsx";
import { DataGridLoadingOverlay } from "src/components/DataGrid/DataGridLoadingOverlay/DataGridLoadingOverlay.tsx";
import { convertDataGridColumns } from "src/components/DataGrid/convertDataGridColumns.tsx";
import type {
  ButtonV2Skin,
  ButtonV2Variant,
} from "src/components/ButtonV2/ButtonV2.tsx";
import type { GridSortModel } from "@mui/x-data-grid/models";
import { loadTranslations } from "src/lib/i18next/loadTranslations.ts";
import { deepEq } from "@exonn/utils";
import { toJS } from "mobx";
import type { GridCallbackDetails } from "@mui/x-data-grid/models/api";
import type { SelectV2Props } from "src/components/SelectV2/SelectV2.tsx";
import type { SelectLabelProp, SelectValueProp } from "src/system/system.types";
import type { TextFilterModeEnum } from "@exonn/js-sdk";

await loadTranslations(TranslationNamespaceEnum.DataGridComponent);

export type DataGridContextMenuItem<R extends GridValidRowModel = any> = {
  key: string;
  label: ReactNode;
  danger?: boolean;
  icon: ReactNode;
  hide?: (row: R) => boolean;
  onSelect: (row: R) => void;
  onConfirm?: (row: R) => void;
  ConfirmComponent?: React.ComponentType<{
    onConfirm: () => void;
    onCancel: () => void;
    onClose: () => void;
    row: R;
  }>;
};

export type DataGridProps<R extends GridValidRowModel = any> = Omit<
  MuiDataGridPremiumProps<R>,
  "columns" | "slots" | "slotProps"
> & {
  columns: GridColDef<R>[];
  hideToolbar?: boolean;
  appliedFilterModel?: GridFilterModel;
  onAppliedFilterModelChange?: (appliedFilters: GridFilterModel) => void;

  contextMenu?: DataGridContextMenuProps<R>;
  hasError?: boolean;
  slots?: Partial<GridPremiumSlotsComponent> & {
    errorOverlay?: JSXElementConstructor<any>;
  };
  slotProps?: MuiDataGridPremiumProps["slotProps"] & {
    noRowsOverlay?: GridSlotProps["noRowsOverlay"];
  };
  showNoInitialDataLoadedOverlay?: boolean;
  onLoadInitialData?: () => void;
  hideToolbarCreateNewRecordButton?: boolean;
  toolbarStartAdditionalElements?: ReactNode[];
  hideToolbarCsvButton?: boolean;
  hideToolbarRefreshButton?: boolean;
  onToolbarExportCsv?: () => void;
  onToolbarCreateNewRecord?: () => void;
  hideToolbarPagination?: boolean;
  onToolbarRefresh?: () => void;
  disableToolbarPagination?: boolean;
  disableToolbarCreateNewRecordButton?: boolean;
  disableToolbarRefreshButton?: boolean;
  createToolbarNewRecordText?: string;
  createToolbarNewRecordSkin?: ButtonV2Skin;
  createToolbarNewRecordVariant?: ButtonV2Variant;
  createToolbarNewRecordType?: HTMLButtonElement["type"];

  noRowsOverlayTitle?: string;

  i18n?: I18n;
  store?: DataGridStore<R>;
  error?: Error | null;
  disableColumnHeaderFilter?: boolean;
  onRecoverFromError?: () => void;
};

export function DataGrid<R extends GridValidRowModel = GridValidRowModel>(
  props: DataGridProps<R>,
) {
  const theme = useTheme();
  const {
    columns: passedColumns,
    getRowClassName: getRowClassNamePassed,
    apiRef: passedApiRef,
    slots: passedSlots,
    slotProps: passedSlotProps,
    filterMode: filterModeProp = "client",
    disableColumnHeaderFilter,
    store = new DataGridStore<R>(),
    autoPageSize,
    onPaginationModelChange: onPaginationModelChangeProp,
    error,
    onRecoverFromError,
    appliedFilterModel = {
      items: [],
    },
    onAppliedFilterModelChange,
    contextMenu: contextMenuProp,
    hideToolbar = false,
    loading = false,
    createToolbarNewRecordText,
    hideToolbarCsvButton,
    hideToolbarRefreshButton,
    hideToolbarPagination,
    createToolbarNewRecordType,
    createToolbarNewRecordSkin,
    createToolbarNewRecordVariant,
    getCellClassName: getCellClassNamePassed,
    onToolbarExportCsv,
    onToolbarCreateNewRecord,
    onToolbarRefresh,
    hideToolbarCreateNewRecordButton,
    toolbarStartAdditionalElements,
    disableToolbarPagination,
    disableToolbarRefreshButton,
    disableToolbarCreateNewRecordButton,
    style,
    noRowsOverlayTitle,
    i18n = i18nInstance,
    showNoInitialDataLoadedOverlay = false,
    onFilterModelChange,
    onLoadInitialData,
    onRowClick,
    onRowDoubleClick,
    ...rest
  } = props;

  useEffect(() => {
    if (!deepEq(appliedFilterModel, toJS(store.appliedFilterModel))) {
      store.setAppliedFilterModel(appliedFilterModel);
    }
  }, [appliedFilterModel, store]);

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel, details: GridCallbackDetails<"filter">) => {
      if (showNoInitialDataLoadedOverlay) {
        onLoadInitialData?.();
      }
      onFilterModelChange?.(model, details);
      setFilterModel(model);
    },
    [onFilterModelChange, onLoadInitialData, showNoInitialDataLoadedOverlay],
  );
  useEffect(() => {
    return store.emitter.on("appliedFilterModelChanged", (appliedFilters) => {
      onAppliedFilterModelChange?.(appliedFilters);
    });
  }, [store, onAppliedFilterModelChange]);

  const rowContextMenu = useDataGridRowContextMenu(
    contextMenuProp ?? {
      items: [],
    },
  );

  const localApiRef = useGridApiRef();
  const { t } = useTranslation();
  const apiRef = passedApiRef || localApiRef;

  const muiTheme = createTheme(
    {
      palette: {
        divider: theme.colors.border.muted,
      },
      typography: {
        fontFamily: theme.fonts.normal,
      },
      components: {
        MuiPaper: {
          defaultProps: {
            variant: "outlined", // removes column menu panel shadow
          },
        },
      },
    },
    i18n.language === "de" ? deDE : enUS,
  );

  const columns: MuiGridColDef<R>[] = useMemo(
    () => convertDataGridColumns<R>(passedColumns, t),
    [passedColumns, t],
  );

  const initialColumns = useRef(columns);

  useEffect(() => {
    if (apiRef.current) {
      const state = apiRef.current.exportState();
      apiRef.current.updateColumns(
        columns.map((column) => {
          // restore previous column width when columns change
          // for some reason mui automatically resets columns width when columns change
          // https://github.com/mui/mui-x/issues/14452
          const prevWidth = state?.columns?.dimensions?.[column.field]?.width;
          return {
            ...column,
            width: prevWidth,
          };
        }),
      );
    }
  }, [columns, apiRef]);

  const getRowClassName: DataGridProps["getRowClassName"] = useCallback(
    (params: GridRowClassNameParams) => {
      const rowNode = apiRef.current?.getRowNode(params.id);
      const parentNode =
        rowNode &&
        isLeafNode(rowNode) &&
        apiRef.current?.getRowNode(rowNode.parent);

      const isExpandedRow =
        rowNode && isGroupNode(rowNode) && rowNode.childrenExpanded;

      const isLastRow =
        parentNode &&
        rowNode &&
        isLeafNode(rowNode) &&
        isGroupNode(parentNode) &&
        isLastRowInGroup(parentNode, rowNode);

      return [
        isExpandedRow && dataGridClasses.expandedRow,
        isLastRow && dataGridClasses.lastRowInGroup,
        params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd",
        getRowClassNamePassed?.(params),
      ]
        .filter(Boolean)
        .join(" ");
    },
    [apiRef, getRowClassNamePassed],
  );

  const appliedFilterColumns = useMemo(
    () =>
      (filterModeProp === "client"
        ? filterModel
        : appliedFilterModel
      ).items.map((item) => item.field),
    [appliedFilterModel, filterModel, filterModeProp],
  );

  const getCellClassName: DataGridProps["getCellClassName"] = useCallback(
    (params: GridCellParams<any, any>) => {
      const isAppliedFilter = appliedFilterColumns.includes(params.field);
      return [
        isAppliedFilter && dataGridClasses.appliedFilterCell,
        getCellClassNamePassed?.(params),
      ]
        .filter(Boolean)
        .join(" ");
    },
    [getCellClassNamePassed, appliedFilterColumns],
  );

  const DefaultErrorOverlay = () => (
    <DataGridErrorOverlay onReload={onRecoverFromError} />
  );

  const NoInitialDataLoadeOverlay = () => {
    return <DataGridEnableDataLoadingOverlay onEnable={onLoadInitialData} />;
  };

  const errorOverlay = passedSlots?.errorOverlay || DefaultErrorOverlay;

  const slots = useMemo<DataGridProps["slots"]>(
    () => ({
      columnSortedDescendingIcon: ColumnSortedDescendingButton,
      columnSortedAscendingIcon: ColumnSortedAscendingButton,
      columnUnsortedIcon: ColumnUnsortedButton,
      columnMenuManageColumnsIcon: ManageColumnsIcon,
      columnMenuHideIcon: HideColumnIcon,
      columnMenuSortAscendingIcon: MenuSortAscendingIcon,
      columnMenuSortDescendingIcon: MenuSortDescendingIcon,
      columnMenuFilterIcon: MenuFilterIcon,
      baseIconButton: ButtonIcon,
      baseCheckbox: DataGridCheckbox,
      //panel: DataGridPanel,
      basePopper: DataGridPopper,
      columnMenuPinLeftIcon: PinLeftIcon,
      columnMenu: CustomColumnMenu,
      columnFilteredIcon: ColumnFilteredIcon,
      columnMenuPinRightIcon: PinRightIcon,
      // loadingOverlay: LoadingOverlay,
      columnMenuIcon: ColumnMenuIcon,
      // todo: wrap every passed slot with ThemeProvider and our theme
      // it will decrease the change of app crushing
      ...passedSlots,
      noResultsOverlay: showNoInitialDataLoadedOverlay
        ? NoInitialDataLoadeOverlay
        : error
        ? errorOverlay
        : passedSlots?.noResultsOverlay || DataGridNoResultsOverlay,
      noRowsOverlay: showNoInitialDataLoadedOverlay
        ? NoInitialDataLoadeOverlay
        : error
        ? errorOverlay
        : passedSlots?.noRowsOverlay || DataGridNoRowsOverlay,
    }),
    [passedSlots, error, errorOverlay, showNoInitialDataLoadedOverlay],
  );

  const slotProps = useMemo<DataGridProps["slotProps"]>(() => {
    return {
      ...(disableColumnHeaderFilter && {
        headerFilterCell: {
          InputComponentProps: {
            disabled: true,
          },
        },
      }),
      ...passedSlotProps,
      columnsManagement: {
        disableShowHideToggle: true,
        disableResetButton: true,
        ...passedSlotProps?.columnsManagement,
      },
      // todo: should I delete props that start with noRowsOverlay...
      noRowsOverlay: {
        title: noRowsOverlayTitle,
        ...passedSlotProps?.noRowsOverlay,
      },
    };
  }, [passedSlotProps, disableColumnHeaderFilter, noRowsOverlayTitle]);

  const handleViewportInnerSizeChange = useCallback(() => {
    if (autoPageSize) {
      onPaginationModelChangeProp?.(
        {
          ...apiRef.current.state.pagination.paginationModel,
          pageSize: computePageSize(apiRef.current.getRootDimensions()),
        },
        {
          api: apiRef.current,
          reason: "autoPageSize",
        },
      );
    }
  }, [autoPageSize, apiRef, onPaginationModelChangeProp]);

  const handleRowClick: DataGridPremiumProps<R>["onRowClick"] = (
    params,
    event,
    details,
  ) => {
    onRowClick?.(params, event, details);
    if (contextMenuProp) {
      rowContextMenu.open(params, event);
    }
  };

  const handleRowDoubleClick: DataGridPremiumProps<R>["onRowDoubleClick"] = (
    ...args
  ) => {
    onRowDoubleClick?.(...args);
    if (contextMenuProp) {
      rowContextMenu.close();
    }
  };

  useLayoutEffect(() => {
    return apiRef?.current.subscribeEvent(
      "viewportInnerSizeChange",
      handleViewportInnerSizeChange,
    );
  }, [apiRef, handleViewportInnerSizeChange]);

  return (
    <>
      <StyledEngineProvider>
        <MuiThemeProvider theme={muiTheme}>
          <DataGridStoreProvider store={store}>
            <div style={style} css={styles.container}>
              <DataGridLoadingOverlay
                isLoading={loading}
                title={t("advices.pleaseWait", {
                  ns: TranslationNamespaceEnum.Common,
                })}
              />
              {!hideToolbar && (
                <DataGridToolbar
                  apiRef={apiRef}
                  filterMode={filterModeProp}
                  hideRefreshButton={hideToolbarRefreshButton}
                  startAdditionalElements={toolbarStartAdditionalElements}
                  disableCreateNewRecordButton={
                    disableToolbarCreateNewRecordButton
                  }
                  disablePagination={disableToolbarPagination}
                  disableRefreshButton={disableToolbarRefreshButton}
                  createNewRecordText={createToolbarNewRecordText}
                  createNewRecordSkin={createToolbarNewRecordSkin}
                  createNewRecordVariant={createToolbarNewRecordVariant}
                  createNewRecordType={createToolbarNewRecordType}
                  hideCreateNewRecordButton={hideToolbarCreateNewRecordButton}
                  hideCsvButton={hideToolbarCsvButton}
                  hidePagination={hideToolbarPagination}
                  onRefresh={onToolbarRefresh}
                  onCreateNewRecord={onToolbarCreateNewRecord}
                  onExportCsv={onToolbarExportCsv}
                />
              )}

              <StyledDataGridPremium<R>
                css={styles.grid({
                  hideToolbar,
                  appliedFilterColumns: appliedFilterColumns,
                })}
                headerFilterHeight={theme.dataGrid.headerFilterRowHeight}
                rowHeight={theme.dataGrid.rowHeight}
                columnHeaderHeight={theme.dataGrid.headerRowHeight}
                showCellVerticalBorder
                onFilterModelChange={handleFilterModelChange}
                showColumnVerticalBorder
                getRowClassName={getRowClassName}
                getCellClassName={getCellClassName}
                apiRef={apiRef}
                columns={initialColumns.current}
                pagination
                slots={slots}
                filterMode={filterModeProp}
                disableColumnFilter
                slotProps={slotProps}
                headerFilters
                hideFooter
                disableMultipleColumnsSorting
                disableAggregation
                disableRowGrouping
                onPaginationModelChange={onPaginationModelChangeProp}
                onRowClick={handleRowClick}
                onRowDoubleClick={handleRowDoubleClick}
                {...rest}
              />
              {rowContextMenu.Element}
            </div>
          </DataGridStoreProvider>
        </MuiThemeProvider>
      </StyledEngineProvider>
    </>
  );
}

const StyledDataGridPremium = styled(MuiDataGridPremium)(({ theme }) => {
  return {
    [`&.${gridClasses.root}`]: {
      background: "white",
    },
    [`& .${gridClasses.row}:last-child`]: {
      [`.${gridClasses.cell}`]: {
        borderBottom: "none!important",
      },
    },
    [`& .${gridClasses.cellEmpty}`]: {
      borderRight: `none!important`,
    },
    [`& .${gridClasses["filler--pinnedLeft"]}`]: {
      borderRight: "none", // fixes https://github.com/mui/mui-x/issues/12847
    },
    [`& .${gridClasses["filler--pinnedRight"]}`]: {
      borderLeft: "none", // fixes https://github.com/mui/mui-x/issues/12847
    },
    [`& .${gridClasses.cell}[data-field="${GRID_TREE_DATA_GROUPING_FIELD}"]`]: {
      overflow: "visible",
      padding: "0px!important",
    },
    [`& .${gridClasses.cell}`]: {
      padding: "0 10px!important",
      display: "inline-block",
      //height: "100%",
      width: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      ":focus, :focus-within": {
        outline: "none",
      },
    },

    [`& .${gridClasses.columnHeaders}`]: {
      borderBottom: "1px solid var(--DataGrid-rowBorderColor)",
    },
    [`& .${gridClasses.headerFilterRow}`]: {
      [`& .${gridClasses.columnHeader}`]: {
        borderTop: `none`,
        padding: 0,
        outline: "none",
      },
      [`& .${gridClasses.filler}`]: {
        borderTop: `none`,
      },
      borderTop: "none",
    },
    [`& .${gridClasses.columnHeader}`]: {
      ":hover, :focus, :focus-within": {
        outline: "none",
      },
    },
    [`& .MuiDataGrid-row--borderBottom .MuiDataGrid-filler`]: {
      borderBottom: "none!important",
    },
    [`& .MuiDataGrid-row--borderBottom .${gridClasses.columnHeader}`]: {
      borderBottom: "none!important",
    },
    [`& .${gridClasses.columnHeaderTitleContainerContent}`]: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      display: "unset",
    },
    [`& .${gridClasses.columnSeparator}`]: {
      opacity: "0!important",
    },

    // alternate row background styling
    [`.${gridClasses.row}`]: {
      [`.${gridClasses.cell}`]: {
        backgroundColor: "inherit!important",
      },
      [`.${gridClasses.cell}.${dataGridClasses.appliedFilterCell}`]: {
        backgroundColor: theme.colorsV2.orange[25] + "!important",
      },
      "&.even": {
        backgroundColor: theme.colorsV2.grey[25],
      },
      "&.odd": {
        backgroundColor: theme.colorsV2.white,
      },
      ":hover": {
        backgroundColor: theme.colorsV2.grey[50],
      },
      "&.Mui-selected": {
        backgroundColor: theme.colorsV2.grey[100],
        "&:hover": {
          background: theme.colorsV2.grey[100],
        },
      },
    },

    [`.${gridClasses.cell}[data-field="${GRID_DETAIL_PANEL_TOGGLE_FIELD}"]`]: {
      display: "grid",
      placeContent: "center",
    },
  };
}) as typeof MuiDataGridPremium;

function CustomColumnMenu(props: GridColumnMenuProps) {
  return (
    <StyledGridColumnMenu
      {...props}
      slots={{
        // Hide `columnMenuGroupingItem`
        columnMenuGroupingItem: null,
      }}
    />
  );
}

const StyledGridColumnMenu = styled(MuiGridColumnMenu)(({ theme }) => ({
  [`&.${listClasses.root}`]: {
    padding: "0!important",
    borderRadius: "6px!important",
    border: "none!important",
    overflow: "hidden",
  },
  [`& .${dividerClasses.root}`]: {
    margin: "0!important",
    border: "none!important",
  },
  [`& .${menuItemClasses.root}`]: {
    height: "45px",
    borderBottom: `1px solid ${theme.colorsV2.grey[100]}`,
    ":hover": {
      backgroundColor: `${theme.colorsV2.grey[50]}!important`,
    },
    ":last-of-type": {
      borderBottom: "none!important",
    },
  },
  [`& .${listItemIconClasses.root}`]: {
    minWidth: "28px!important;",
    color: `${theme.colorsV2.grey[800]}`,
  },
  [`& .${listItemTextClasses.root} .${typographyClasses.root}`]: {
    fontSize: "14px!important;",
    color: theme.colorsV2.grey[800],
  },
}));

// const LoadingOverlay = ({ color, sx, ...restProps }: GridOverlayProps) => (
//   <DataGridLoadingOverlay {...restProps} />
// );

const ColumnMenuIcon = styled.i`
  font-size: 15px;
`;
ColumnMenuIcon.defaultProps = {
  className: "fi fi-rs-menu-dots-vertical",
};

const ColumnFilteredIcon = () => <i className={"icon-15-filter"} />;

const ManageColumnsIcon = () => <i className={"icon-15-columns"} />;

const MenuSortAscendingIcon = () => <i className={"icon-15-sort_up"} />;

const MenuSortDescendingIcon = () => <i className={"icon-15-sort_dwn"} />;

const MenuFilterIcon = () => <i className={"icon-15-filter"} />;

const HideColumnIcon = () => <i className={"icon-15-hide_columns"} />;

const PinLeftIcon = styled(() => <i className={"icon-15-thumbtack"} />)();

const PinRightIcon = styled(() => <i className={"icon-15-thumbtack_l"} />)`
  transform-origin: center;
  transform: scaleX(-1);
`;

const ButtonIcon = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.gray[400]};
  display: flex;
  padding: 7px;
  margin: 0 3px;
  border-radius: 100%;
  :hover {
    background: ${({ theme }) => theme.colors.canvas.default};
  }
`;

export const ColumnSortedAscendingButton = () => {
  return <i className={`icon-15-sort_up ${gridClasses.sortIcon}`} />;
};

export const ColumnUnsortedButton = () => {
  return <i className={`icon-15-sort_up ${gridClasses.sortIcon}`} />;
};

export const ColumnSortedDescendingButton = (props: any) => {
  return (
    <i {...props} className={`icon-15-sort_dwn ${gridClasses.sortIcon}`} />
  );
};
